const UNSERIALIZABLE_DATA = '[Unserializable]'

/**
 * Serialize log records to a JSON body. Always succeeds.
 *
 * Web gets this from `@datadog/browser-logs`; the raw-`fetch` RN transport has
 * no such layer and serializes inside the caller's `log()`, so a throw would
 * land in a user flow.
 */
export function serializeLogBatch(batch: readonly object[]): string {
  try {
    return JSON.stringify(batch, createSafeJsonReplacer())
  } catch {
    return `[${batch.map(serializeRecord).join(',')}]`
  }
}

/** Attribute by attribute, so one bad value can't drop unrelated records. */
function serializeRecord(record: object): string {
  const attributes = Object.entries(record).flatMap(([key, value]) => {
    const serialized = serializeValue(value)
    // Omit undefined, as JSON.stringify does for object properties.
    return serialized === undefined
      ? []
      : [`${JSON.stringify(key)}:${serialized}`]
  })
  return `{${attributes.join(',')}}`
}

function serializeValue(value: unknown): string | undefined {
  try {
    return JSON.stringify(value, createSafeJsonReplacer())
  } catch {
    return JSON.stringify(UNSERIALIZABLE_DATA)
  }
}

/**
 * Resolves what `JSON.stringify` can't, inside the traversal it already makes.
 * `toJSON` runs before the replacer, so a throwing one escapes to the callers
 * above. Create one per `stringify`.
 */
function createSafeJsonReplacer() {
  const ancestors: object[] = []

  return function (this: unknown, _key: string, value: unknown): unknown {
    if (typeof value === 'bigint') {
      return value.toString()
    }
    if (typeof value === 'function') {
      return `[Function] ${value.name || 'anonymous'}`
    }
    if (typeof value === 'symbol') {
      return `[Symbol] ${value.description ?? ''}`
    }
    if (value === null || typeof value !== 'object') {
      return value
    }

    // React values reference the fiber that rendered them, or themselves —
    // internals either way, so mark the family instead of walking it.
    if ('$$typeof' in value) {
      return '[React]'
    }

    // `this` is the object `value` came from; unwinding to it leaves only true
    // ancestors, so a repeated sibling reference isn't read as a cycle.
    while (ancestors.length > 0 && ancestors[ancestors.length - 1] !== this) {
      ancestors.pop()
    }
    if (ancestors.includes(value)) {
      return '[Circular]'
    }
    ancestors.push(value)
    return value
  }
}
